import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, ScrollText, AlertTriangle, FileWarning } from "lucide-react";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";

const SECTIONS = [
  {
    id: "terms",
    icon: ScrollText,
    label: "Terms of Service",
    body: [
      "By creating a TradingoX account you agree to these Terms. If you do not agree, do not use the platform.",
      "Eligibility. You must be at least 18 years old and legally able to enter binding agreements in your jurisdiction. You are responsible for the accuracy of the information you provide at signup, including your full name, address, city, state, phone, and email.",
      "Account & Security. You are responsible for keeping your password confidential and for all activity under your account. Notify us immediately at mail@charlesthao.com if you suspect unauthorized access.",
      "License. We grant you a personal, non-transferable, revocable license to use TradingoX for your own trading activity. You may not resell, reverse-engineer, or use the platform for automated scraping of third-party data.",
      "Termination. We may suspend or close accounts that violate these Terms, submit false information, or engage in fraudulent activity. You may close your account at any time by contacting support.",
    ],
  },
  {
    id: "conditions",
    icon: FileWarning,
    label: "Platform Conditions",
    body: [
      "Brokerage & Custody. TradingoX is a technology platform, not a broker, exchange, custodian, or investment adviser. All trades are executed at your linked brokerage (Alpaca). Your broker — never TradingoX — holds custody of your funds and securities.",
      "Fees. TradingoX is free to join. We collect a 1.25% service fee on the net profit of profitable trades only. Losing trades cost nothing. Fees settle via Stripe auto-pay when your balance passes $10. See the FAQ for the full breakdown.",
      "AI Signals. TradingoX uses Claude Sonnet 5 to generate trading signals based on Alpha Vantage market data. Signals are informational only, may contain errors, and do not guarantee outcomes.",
      "Automated Trading. If you enable the automated scalping bot, you authorize TradingoX to place orders on your Alpaca account within the risk parameters you configure (take-profit %, stop-loss %, position size %, confidence threshold). You may pause or disable the bot at any time.",
      "Referrals. Both the referrer and the referred user earn 7 fee-free trading days once the referred user closes their first profitable trade. Cash referral commissions are not paid at this time.",
    ],
  },
  {
    id: "warning",
    icon: AlertTriangle,
    label: "Trading Risk Warning",
    body: [
      "Trading stocks is highly risky. You can lose some, all, or more than the money you deposit. Never trade money you cannot afford to lose. Never borrow money to trade. Do not use funds intended for essential expenses (rent, food, medical, education).",
      "Past performance does not predict future results. Historical win rates, backtests, and demo/paper results are not guarantees of live outcomes. Every trader's experience will be different.",
      "No guarantee of profit. TradingoX makes no representation that any strategy, signal, or configuration will produce profits. Automated trading can amplify losses just as it can amplify gains.",
      "Market risk. Markets are volatile. Prices can gap through your stop-loss during news events, halts, or off-hours. Slippage, connectivity issues, and API outages can cause fills at unexpected prices.",
      "You are responsible for your trades. Even when using the automated bot, all trading decisions and outcomes are your responsibility. Consult a licensed financial adviser if unsure whether trading is appropriate for you.",
    ],
  },
  {
    id: "disclaimer",
    icon: ShieldAlert,
    label: "Disclaimer & Limitation of Liability",
    body: [
      "Not investment advice. Nothing on TradingoX — including AI signals, chatbot replies from Maiv, training materials, dashboards, or founder notes — constitutes investment, legal, tax, or accounting advice. Content is for informational and educational purposes only.",
      "No fiduciary relationship. TradingoX does not act as your fiduciary, broker-dealer, investment adviser, or financial planner.",
      "Third-party services. TradingoX integrates with Alpaca (brokerage), Alpha Vantage (market data), Anthropic (AI), Stripe (payments), and Resend (email). We are not responsible for outages, errors, or losses caused by third-party services.",
      "As-is basis. The platform is provided \"as is\" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.",
      "Limitation of liability. To the maximum extent permitted by law, TradingoX, its founders, employees, and affiliates are not liable for any indirect, incidental, consequential, special, or punitive damages, or for any lost profits, trading losses, data loss, or business interruption arising from your use of the platform.",
      "Governing law. These Terms are governed by the laws of the State of Tennessee, United States, without regard to conflict-of-law principles. Disputes will be resolved in the state or federal courts located in Shelby County, Tennessee.",
      "Contact. Questions about these Terms? Email mail@charlesthao.com or text 901-361-9173.",
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col">
      <RiskBanner />
      <header className="max-w-5xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" data-testid="terms-back-home" className="flex items-center gap-3 group">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          data-testid="terms-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </header>

      <main className="max-w-3xl w-full mx-auto px-6 pt-8 pb-16 flex-1">
        <div className="mb-10 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-2">
            legal
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Terms, Conditions, <span className="text-emerald-400">Warning &amp; Disclaimer</span>
          </h1>
          <p className="mt-3 text-slate-400 text-base max-w-2xl mx-auto">
            Please read this page carefully before creating an account or trading with TradingoX.
          </p>
          <p className="mt-1 text-[11px] font-mono text-slate-500">Last updated: September 2026</p>
        </div>

        <nav data-testid="terms-toc" className="mb-10 flex flex-wrap gap-2 justify-center">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`terms-toc-${s.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-emerald-400/40 text-slate-200 hover:text-white text-[12px] font-semibold transition"
            >
              <s.icon className="w-3.5 h-3.5 text-emerald-300" />
              {s.label}
            </a>
          ))}
        </nav>

        <div className="space-y-10">
          {SECTIONS.map((s, idx) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`terms-section-${s.id}`}
              className="rounded-2xl border border-white/10 bg-[#0F111A]/70 backdrop-blur-sm p-6 sm:p-8 scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-300">
                  <s.icon className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
                    Section {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-100">{s.label}</h2>
                </div>
              </div>
              <div className="space-y-3 text-[14px] text-slate-300 leading-relaxed">
                {s.body.map((p, i) => (
                  <p key={`${s.id}-p-${i}`}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 text-center">
          <div className="text-sm text-slate-400 mb-1">Questions about these terms?</div>
          <div className="font-display text-xl font-bold">
            Email <a href="mailto:mail@charlesthao.com" className="text-emerald-300 underline underline-offset-2">mail@charlesthao.com</a>
            {" "}or{" "}
            <a href="sms:+19013619173" className="text-emerald-300 underline underline-offset-2">text 901-361-9173</a>.
          </div>
        </div>
      </main>

      <RiskFooter />
    </div>
  );
}
